import React, { useState } from "react";
import toast from "react-hot-toast";
import { setMpin } from "../../services/authServices";

interface MpinSetupProps {
  onMpinSet: () => void;
  onSkip?: () => void;
}

const MpinSetup: React.FC<MpinSetupProps> = ({ onMpinSet, onSkip }) => {
  const [mpin, setMpinValue] = useState<string>("");
  const [confirmMpin, setConfirmMpin] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showMpin, setShowMpin] = useState<boolean>(false);
  const [showConfirmMpin, setShowConfirmMpin] = useState<boolean>(false);

  const handleMpinChange = (value: string, field: 'mpin' | 'confirm') => {
    // Only allow numeric input and limit to 4 digits
    const numericValue = value.replace(/\D/g, '').slice(0, 4);
    
    if (field === 'mpin') {
      setMpinValue(numericValue);
    } else {
      setConfirmMpin(numericValue);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (mpin.length !== 4) {
      toast.error("MPIN must be 4 digits");
      return;
    }

    if (mpin !== confirmMpin) {
      toast.error("MPIN and confirmation do not match");
      return;
    }

    setIsLoading(true);

    try {
      // Don't send oldMpin key at all when setting for first time
      await setMpin({ mpin });
      toast.success("MPIN set successfully!");
      onMpinSet();
    } catch (error: any) {
      console.error("Error setting MPIN:", error);
      
      // Extract specific error message from API response
      const errorMessage = error.response?.data?.message || "Failed to set MPIN. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container">
      <div className="row justify-content-center login_section">
        <div className="col-12 col-md-6 col-lg-5 col-xl-4 login_card">
          <div
            className="card login-card shadow-lg p-4"
            style={{
              maxWidth: "650px",
              width: "100%",
              margin: "0 auto",
              borderRadius: "15px",
              backgroundColor: "#ebeff4",
            }}
          >
            <div className="d-flex flex-column align-items-center" style={{ width: "100%" }}>
              <img
                src="assets/img/3sigma_logo.png"
                alt="logo"
                className="mb-4"
                style={{
                  width: "100%",
                  maxWidth: "200px",
                  height: "auto",
                  objectFit: "contain",
                }}
              />
              <div className="login-header text-center mb-4">
                <h2 style={{ fontWeight: "bold", color: "#333", fontSize: "1.75rem" }}>
                  Set Your MPIN
                </h2>
                <p className="text-muted mt-2" style={{ fontSize: "0.95rem" }}>
                  Create a 4-digit MPIN for quick login next time
                </p>
              </div>
              
              <form onSubmit={handleSubmit} className="login_form w-100">
                <div className="form_container_login">
                  <div className="mb-3">
                    <label className="form-label" style={{ fontWeight: "600", color: "#333" }}>
                      Enter 4-digit MPIN
                    </label>
                    <input
                      type="password"
                      className="form-control"
                      value={mpin}
                      onChange={(e) => handleMpinChange(e.target.value, 'mpin')}
                      placeholder="****"
                      maxLength={4}
                      style={{
                        height: "50px",
                        fontSize: "18px",
                        textAlign: "center",
                        letterSpacing: "8px",
                        borderRadius: "8px",
                        border: "1px solid #ddd",
                      }}
                    />
                  </div>
                  
                  <div className="mb-3">
                    <label className="form-label" style={{ fontWeight: "600", color: "#333" }}>
                      Confirm MPIN
                    </label>
                    <input
                      type="password"
                      className="form-control"
                      value={confirmMpin}
                      onChange={(e) => handleMpinChange(e.target.value, 'confirm')}
                      placeholder="****"
                      maxLength={4}
                      style={{
                        height: "50px",
                        fontSize: "18px",
                        textAlign: "center",
                        letterSpacing: "8px",
                        borderRadius: "8px",
                        border: "1px solid #ddd",
                      }}
                    />
                  </div>
                  
                  <button
                    type="submit"
                    className="btn btn-primary w-100 mt-3"
                    style={{
                      backgroundColor: "#3A4B86",
                      fontWeight: "bold",
                      fontSize: "1.1rem",
                      borderRadius: "8px",
                      height: "50px",
                    }}
                    disabled={isLoading || mpin.length !== 4 || confirmMpin.length !== 4}
                  >
                    {isLoading ? "Setting MPIN..." : "Set MPIN"}
                  </button>
                  
                  {onSkip && (
                    <button
                      type="button"
                      className="btn btn-outline-secondary w-100 mt-2"
                      style={{
                        fontWeight: "bold",
                        fontSize: "1rem",
                        borderRadius: "8px",
                        height: "50px",
                      }}
                      onClick={onSkip}
                    >
                      Skip for now
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MpinSetup;